import { exchangeToken, login, register, useUser } from 'hooks/useAccount';
import { permissionArrayObject, Role, UserInfo } from 'model/account';
import React, {
  useContext,
  createContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from './auth';
import {
  getPatientInfo,
  PatientInfo,
  usePatientInfo,
  listFamilies,
  createFamily,
} from 'hooks/usePatient';
import { HomeLinkRoutes } from 'constants/routes';
import strings from 'constants/strings';
import { handleErrorToast } from 'components/Toast';
import { useChat } from './ChatProvider';
import { isDevEnvironment } from './config';
import { PatientInfoRecord, usePatientInfos } from 'hooks/usePatientInfo';
import { queryClient } from 'utils/reactQueryClient';

const AuthContext = createContext<{
  devMode?: boolean;
  setDevMode: (devMode: boolean) => void;
  token?: string;
  user?: UserInfo;
  role?: Role;
  loginAction: (data: { email: string; password: string }) => Promise<boolean>;
  registerAction: (data: {
    email: string;
    password: string;
    verificationCode: string;
  }) => Promise<boolean>;
  exchangeTokenAction: (
    jwtToken: string,
    devMode?: boolean,
  ) => Promise<boolean>;
  logOut: () => void;
  patientInfo?: PatientInfo;
  defaultPatientInfo?: PatientInfo;
  defaultPatientInfoId?: string;
  patientInfos?: PatientInfoRecord[];
  isMobileApp?: boolean;
  acceptedTerms: boolean;
  showChatButton: boolean;
  showProfileButton: boolean;
}>({
  devMode: false,
  setDevMode: () => {},
  token: undefined,
  user: undefined,
  role: undefined,
  loginAction: () => Promise.resolve(false),
  registerAction: () => Promise.resolve(false),
  exchangeTokenAction: () => Promise.resolve(false),
  logOut: () => {},
  patientInfo: undefined,
  defaultPatientInfo: undefined,
  defaultPatientInfoId: undefined,
  patientInfos: [],
  isMobileApp: false,
  acceptedTerms: false,
  showChatButton: false,
  showProfileButton: false,
});

export const getRoleBasedOnUser = (user?: UserInfo) => {
  if (user?.permissions?.includes(permissionArrayObject.doctor)) {
    return Role.DOCTOR;
  }
  if (user?.permissions?.includes(permissionArrayObject.drug)) {
    return Role.PHARMACIST;
  }
  return Role.PATIENT;
};

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [devMode, setDevMode] = useState(isDevEnvironment() || false);
  const [token, setToken] = useState<string | undefined>(
    AuthService.ljToken() || '',
  );
  const [isMobileApp, setIsMobileApp] = useState(false);
  const { user, refetch } = useUser(token);
  const {
    patientInfo,
    refetch: refetchPatientInfo,
    isLoading,
  } = usePatientInfo();
  const { patientInfos } = usePatientInfos(token);
  const navigate = useNavigate();
  const { resetAllData } = useChat();
  const role = useMemo(() => {
    if (!user) return undefined;
    return getRoleBasedOnUser(user);
  }, [user]);

  const defaultPatientInfoRecord = useMemo(() => {
    return patientInfos?.find((p) => p.isDefault) ?? undefined;
  }, [patientInfos]);

  const loginAction = async (data: {
    email: string;
    password: string;
  }): Promise<boolean> => {
    try {
      const { data: resData } = await login(data);
      AuthService.ljToken(resData.token || undefined);
      setToken(resData.token || undefined);
      setDevMode(isDevEnvironment() ?? false);
      await getUserInfo();
      setDevMode(isDevEnvironment() || false);

      // List families and create one if empty
      try {
        const families = await listFamilies();
        if (!families || families.length === 0) {
          await createFamily({ name: 'my home' });
        }
      } catch (familyErr) {
        // Log but don't fail login if family creation fails
        console.error('Failed to setup family:', familyErr);
      }

      return true;
    } catch (err) {
      console.error(err);
      handleErrorToast(err, strings.error.invalid_password);
      return false;
    }
  };

  const registerAction = async (data: {
    email: string;
    password: string;
    verificationCode: string;
  }): Promise<boolean> => {
    try {
      const { data: resData } = await register(data);
      AuthService.ljToken(resData.token || undefined);
      setToken(resData.token || undefined);
      await refetch();
      return true;
    } catch (err) {
      console.error(err);
      handleErrorToast(err, strings.error.invalid_verification_code);
      return false;
    }
  };

  const exchangeTokenAction = async (jwtToken: string, devMode?: boolean) => {
    setDevMode((isDevEnvironment() || devMode) ?? false);
    try {
      const { data: resData } = await exchangeToken(jwtToken);
      AuthService.ljToken(resData.token || undefined);
      setToken(resData.token || undefined);
      queryClient.clear();
      setIsMobileApp(true);
      await getUserInfo();
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const logOut = () => {
    setDevMode(false);
    setToken(undefined);
    AuthService.logout();
    navigate(HomeLinkRoutes.login);
  };

  const getUserInfo = async () => {
    try {
      await refetch();
    } catch (err) {
      console.error(err);
      logOut();
    }
  };

  useEffect(() => {
    if (token && !user) {
      getUserInfo();
    }
  }, [token]);

  const acceptedTerms = useMemo(() => {
    // return !!patientInfo && Object.keys(patientInfo || {}).length > 0;
    return true;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        devMode,
        setDevMode,
        token,
        user,
        role,
        loginAction,
        registerAction,
        exchangeTokenAction,
        logOut,
        patientInfo,
        defaultPatientInfo: defaultPatientInfoRecord?.info,
        defaultPatientInfoId: defaultPatientInfoRecord?.id,
        patientInfos,
        isMobileApp,
        acceptedTerms,
        showChatButton: true,
        showProfileButton: false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
